export const CoreErrorCause = {
  InvalidCredential: 'InvalidCredential',
  EmailAlreadyInUse: 'EmailAlreadyInUse',
  WeakPassword: 'WeakPassword',
  RequireRecentAuthentication: 'RequireRecentAuthentication',
  PartnershipNotFound: 'PartnershipNotFound',
  InvalidInvitationCode: 'InvalidInvitationCode',
  InvitationCodeAlreadyUsed: 'InvitationCodeAlreadyUsed',
  PartnerNotFound: 'PartnerNotFound',
  InvalidPartnershipStatus: 'InvalidPartnershipStatus',
  DataNotFound: 'DataNotFound',
  Forbidden: 'Forbidden',
  NetworkConnection: 'NetworkConnection',
  PermissionDenied: 'PermissionDenied',
  NetworkError: 'NetworkError',
  PushNotificationsRequestFailure: 'PushNotificationsRequestFailure',
  ConstantsCouldNotBeParsed: 'ConstantsCouldNotBeParsed',
  PurchaseLoginFailed: 'PurchaseLoginFailed',
  FetchingPackagesFailed: 'FetchingPackagesFailed',
  PurchaseOfferingsFetchFailed: 'PurchaseOfferingsFetchFailed',
  PurchasedPackageCannotBeFound: 'PurchasedPackageCannotBeFound',
  PurchaseCancelledByUser: 'PurchaseCancelledByUser',
  PurchaseFailed: 'PurchaseFailed',
  PurchaseRestoreFailed: 'PurchaseRestoreFailed',
  NoActiveSubscriptionFound: 'NoActiveSubscriptionFound',
  PlatformNotSupported: 'PlatformNotSupported',
  PurchaseConfirmationFailed: 'PurchaseConfirmationFailed',
  PurchaseGrantTimeout: 'PurchaseGrantTimeout',
  CreditDecrementFailed: 'CreditDecrementFailed',
  ProductCouldNotBeParsed: 'ProductCouldNotBeParsed',
  Unknown: 'Unknown',
} as const

export type CoreErrorCause =
  (typeof CoreErrorCause)[keyof typeof CoreErrorCause]

interface SentryScopeLike {
  setTag?: (key: string, value: string) => void
  setLevel?: (level: 'error' | 'warning') => void
  setContext?: (key: string, context: Record<string, unknown>) => void
  setExtra?: (key: string, extra: unknown) => void
}

interface SentryLike {
  withScope: (callback: (scope: SentryScopeLike) => void) => void
  captureException: (error: unknown) => void
}

function getSentryFromGlobalScope(): SentryLike | null {
  const scopedGlobal = globalThis as typeof globalThis & {
    Sentry?: SentryLike
  }
  return scopedGlobal.Sentry ?? null
}

export interface CoreErrorParams {
  cause: CoreErrorCause
  context?: string
  underlyingException?: unknown
  originalError?: unknown
}

export class CoreError extends Error {
  public readonly cause: CoreErrorCause
  public readonly context?: string
  public readonly underlyingException?: unknown

  private static shouldLog(params: CoreErrorParams): boolean {
    const context = params.context ?? ''
    const isExpectedPartnerRevocation =
      params.cause === CoreErrorCause.PermissionDenied &&
      context.includes('permission_denied at /users/') &&
      context.includes('/profile')

    return !isExpectedPartnerRevocation
  }

  constructor(params: CoreErrorParams) {
    super(params.context ?? params.cause)
    this.name = 'CoreError'
    this.cause = params.cause
    this.context = params.context
    this.underlyingException =
      params.underlyingException ?? params.originalError

    const shouldLog = CoreError.shouldLog(params)
    if (shouldLog) {
      console.error('CoreError created:', {
        cause: this.cause,
        context: this.context,
        underlyingException: this.underlyingException,
        message: this.message,
      })
    }

    const sentry = getSentryFromGlobalScope()
    if (sentry) {
      sentry.withScope((scope) => {
        scope.setTag?.('error_type', 'CoreError')
        scope.setTag?.('core_error_cause', this.cause)
        scope.setLevel?.(shouldLog ? 'error' : 'warning')
        scope.setContext?.('core_error', {
          cause: this.cause,
          context: this.context ?? '',
          message: this.message,
          shouldLog,
        })
        scope.setExtra?.('underlyingException', this.underlyingException)
        sentry.captureException(this)
      })
    }

    const ErrorWithCapture = Error as typeof Error & {
      captureStackTrace?: (target: object, constructorOpt?: object) => void
    }
    if (ErrorWithCapture.captureStackTrace) {
      ErrorWithCapture.captureStackTrace(this, CoreError)
    }
  }
}
